import { useState } from 'react'
import type { CSSProperties } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTag,
  faBuilding,
  faEnvelope,
  faShieldHalved,
  faHelmetSafety,
  faFileInvoice,
  faBoxesStacked,
  faRotate,
  faIndustry,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImageUrl,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontProducts,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track, trackSelectItem } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

const PRODUCT_NAME = 'Sarung Tangan Pabrik'
const HERO_IMAGE = '/lp/sarung-tangan-gekko-spread.jpeg'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Procurement-tailored FAQ. This buyer is a purchasing/HRD/safety officer
// stocking gloves for factory workers, not a reseller, so it answers PO,
// resupply, quotation, and safety-standard questions.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Berapa harga sarung tangan pabrik di Dropify?',
    a: 'Harga mengikuti seri, jumlah, stok, dan kota tujuan. Kirim kebutuhan melalui WhatsApp agar admin dapat menyiapkan quotation.',
  },
  {
    q: 'Berapa harga sarung tangan Gekko Nitrile atau PU?',
    a: 'Harga mengikuti seri, ukuran, dan jumlah. Kirim pilihan Gekko Nitrile atau PU beserta kebutuhan Anda melalui WhatsApp agar admin dapat memeriksa stok dan menyiapkan quotation.',
  },
  {
    q: 'Berapa harga sarung tangan nitrile per box?',
    a: 'Sarung tangan kerja lapis nitrile dan sarung tangan nitrile sekali pakai memiliki kemasan serta ketentuan berbeda. Kirim jenis yang diperlukan dan jumlah kebutuhan agar admin dapat mengonfirmasi satuan pembelian.',
  },
  {
    q: 'Bisa suplai rutin tiap bulan untuk pabrik kami?',
    a: 'Kirim perkiraan kebutuhan bulanan, seri, dan kota tujuan. Admin akan memeriksa ketersediaan sebelum membahas pengadaan berikutnya.',
  },
  {
    q: 'Bisa pakai PO (Purchase Order) dan minta invoice resmi?',
    a: 'Sampaikan kebutuhan quotation, PO, dan dokumen administrasi saat meminta penawaran. Admin akan mengonfirmasi proses yang tersedia untuk pesanan Anda.',
  },
  {
    q: 'Minimal ordernya berapa untuk skala pabrik?',
    a: 'Minimum order mengikuti produk dan kemasan. Lihat ketentuan per kategori atau kirim nama seri serta jumlah kebutuhan agar admin dapat mengonfirmasinya.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Jenisnya bisa disesuaikan sama jenis pekerjaan?',
    a: 'Ya. Sampaikan jenis pekerjaan, kondisi permukaan benda yang ditangani, jumlah pemakai, serta standar yang diwajibkan. Admin akan membantu membandingkan seri yang tersedia.',
  },
  {
    q: 'Apa itu sarung tangan kain?',
    a: 'Sarung tangan kain adalah sebutan umum untuk sarung tangan katun atau rajut benang. Dropify menyuplainya untuk kerja pabrik dengan harga grosir, antara lain melalui brand Diamond dan Matahari.',
  },
  {
    q: 'Merek apa saja yang tersedia?',
    a: 'Kami menyuplai Gekko dengan pilihan lapisan Nitrile dan PU, serta Diamond dan Matahari untuk sarung tangan katun rajut. Marking CE, EN388, dan dokumen pendukung perlu diperiksa berdasarkan seri yang akan dibeli.',
  },
  {
    q: 'Ada sarung tangan nitrile food grade?',
    a: 'Fokus utama kami sarung tangan kerja industri: Gekko Nitrile dan PU, serta sarung tangan katun rajut Diamond dan Matahari. Untuk kebutuhan nitrile food grade, kirim standar dan dokumen wajib melalui WhatsApp agar admin dapat memeriksa produk yang tersedia.',
  },
  {
    q: 'Ada standar keamanannya (K3)?',
    a: 'Dokumen dan standar keselamatan perlu diperiksa per seri. Kirim persyaratan K3 atau tender Anda agar admin dapat mencocokkan barang yang tersedia.',
  },
  {
    q: 'Bisa minta sample dulu sebelum pengadaan besar?',
    a: 'Ketersediaan sample dan biayanya perlu dikonfirmasi berdasarkan seri yang dipilih. Sampaikan jenis pekerjaan agar admin dapat memeriksa pilihan yang relevan.',
  },
  {
    q: 'Bisa kirim ke lokasi pabrik di luar Jawa?',
    a: 'Kirim kota tujuan, jumlah, dan target penerimaan. Admin akan memeriksa pilihan pengiriman serta ongkir sebelum quotation dibuat.',
  },
  {
    q: 'Bisa minta penawaran resmi (quotation) dulu?',
    a: 'Bisa. Kirim nama produk atau seri, jumlah, kota tujuan, target penerimaan, dan kebutuhan dokumen agar quotation dapat disiapkan.',
  },
]

export const Route = createFileRoute('/sarung-tangan-pabrik')({
  loader: async () => {
    // Config failure falls back to null: the contact helpers carry hardcoded
    // defaults, and paid traffic must never land on a 500 because the
    // dashboard API hiccupped.
    const [config, testimonialsRaw, matahari, diamond, cpr5243Red, cpr5243Blue, cpr5243Green, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      fetchBrandProductCached('sarung tangan matahari', 'Sarung Tangan Matahari'),
      fetchBrandProductCached('sarung tangan diamond', 'Sarung Tangan Diamond'),
      fetchBrandProductCached('CPR5243-2', 'Sarung Tangan Industri CPR5243-2 Merah'),
      fetchBrandProductCached('CPR5243-3', 'Sarung Tangan Industri CPR5243-3 Biru'),
      fetchBrandProductCached('CPR5243-4', 'Sarung Tangan Industri CPR5243-4 Hijau'),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      matahari,
      diamond,
      cpr5243Red,
      cpr5243Blue,
      cpr5243Green,
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Supplier & Distributor Sarung Tangan Pabrik')
    const description =
      'Supplier sarung tangan kerja untuk pabrik dan pengadaan. Pilih seri Gekko, Diamond, Matahari, atau CPR5243 berdasarkan pekerjaan, jumlah, dan kebutuhan dokumen.'
    const ogImage = absoluteUrl(HERO_IMAGE)

    return {
      // Indexable: this page targets organic buyers searching "supplier sarung
      // tangan pabrik" and can also serve as a paid-ads destination.
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/sarung-tangan-pabrik') },
        { property: 'og:image', content: ogImage || DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage || DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/sarung-tangan-pabrik') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: FactoryLandingPage,
})

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sesuai kebutuhan',
    desc: 'Kirim jenis pekerjaan, jumlah, dan kota tujuan agar admin dapat memeriksa seri, stok, harga, dan ongkir.',
  },
  {
    icon: faRotate,
    title: 'Rencana kebutuhan berkala',
    desc: 'Kebutuhan bulanan dapat dibahas bersama admin. Admin akan mengecek ketersediaan dan jadwal pengiriman sebelum menyiapkan quotation.',
  },
  {
    icon: faFileInvoice,
    title: 'Kebutuhan dokumen pengadaan',
    desc: 'Sampaikan kebutuhan quotation, PO, atau dokumen produk sejak awal agar admin dapat memeriksa proses yang sesuai.',
  },
  {
    icon: faShieldHalved,
    title: 'Pilih berdasarkan seri',
    desc: 'Gekko, Diamond, Matahari, dan CPR5243 memiliki kegunaan berbeda. Dokumen keselamatan diperiksa per seri.',
  },
]

const FIT_ITEMS = [
  'Pabrik atau industri yang butuh suplai sarung tangan rutin untuk pegawai bagian produksi dan gudang.',
  'Bagian purchasing atau pengadaan yang perlu membandingkan seri, jumlah, quotation, dan kota tujuan.',
  'Petugas atau tim K3 yang perlu memeriksa spesifikasi dan dokumen keselamatan per seri.',
  'Kontraktor, proyek, atau workshop yang perlu sarung tangan kerja dalam jumlah besar dan berkelanjutan.',
  'HRD atau GA yang mengurus perlengkapan kerja pegawai dan perlu menyesuaikan jumlah dengan pemakaian.',
  'Pabrik yang sedang menilai pemasok sarung tangan kerja untuk kebutuhan berikutnya.',
]

type ShowcaseItem = {
  image: string
  title: string
  desc: string
  tag: string
  // Set for cards backed by a real storefront product, so we can link the
  // card to its product page (real photo + internal link).
  href?: string
}

// A real storefront product resolved in the loader, used to build a brand card
// with its actual photo. `image` is already run through toExternalImage.
type BrandProduct = { name: string; slug: string; image: string | null }

const BRAND_CARD_FALLBACK_IMAGE = '/lp/sarung-tangan-katalog.jpeg'
const BRAND_IMAGE_TRANSFORM = { width: 480, format: 'webp', quality: 70 } as const

// dropifyFetch deliberately never caches /storefront/products (katalog and
// search need fresh stock), but these two brand cards don't. Cache them at the
// route level so every ad click doesn't pay two extra API round-trips of TTFB.
// Failed/empty lookups are retried sooner so a transient API error doesn't
// hide the cards for a full hour.
const BRAND_PRODUCT_TTL_MS = 60 * 60 * 1000
const BRAND_PRODUCT_MISS_TTL_MS = 5 * 60 * 1000
const brandProductCache = new Map<string, { expiresAt: number; product: BrandProduct | null }>()

async function fetchBrandProductCached(search: string, exactName: string): Promise<BrandProduct | null> {
  const cached = brandProductCache.get(exactName)
  if (cached && cached.expiresAt > Date.now()) return cached.product
  const product = await fetchBrandProduct(search, exactName)
  const ttlMs = product ? BRAND_PRODUCT_TTL_MS : BRAND_PRODUCT_MISS_TTL_MS
  brandProductCache.set(exactName, { expiresAt: Date.now() + ttlMs, product })
  return product
}

// Resolve a brand's real product from the storefront so its card shows the
// actual photo. Prefers an exact name match, falls back to the first result.
async function fetchBrandProduct(search: string, exactName: string): Promise<BrandProduct | null> {
  try {
    const response = await listStorefrontProducts({ search, per_page: 5 })
    const items = response.items ?? []
    const product = items.find((p) => p.name.trim().toLowerCase() === exactName.toLowerCase()) ?? items[0]
    if (!product) return null
    const url = getProductPrimaryImageUrl(product)
    return {
      name: product.name,
      slug: product.slug || product.id,
      image: url ? toExternalImage(url, BRAND_IMAGE_TRANSFORM) : null,
    }
  } catch {
    return null
  }
}

// The CPR5243 cards use the supplied product photos and gain product-detail
// links as soon as their storefront records are published. Diamond and
// Matahari are spliced in from existing storefront products (see loader).
function buildShowcaseItems(
  matahari: BrandProduct | null,
  diamond: BrandProduct | null,
  cpr5243: Array<BrandProduct | null>,
): ShowcaseItem[] {
  const items: ShowcaseItem[] = [
    {
      image: '/products/sarung-tangan-industri-cpr5243-2.webp',
      href: cpr5243[0] ? `/produk/${cpr5243[0].slug}` : undefined,
      title: 'Sarung Tangan Industri CPR5243-2 Merah',
      desc: 'Sarung tangan kerja size 10 warna merah dengan lapisan hitam pada telapak dan ujung jari. Dapat dipertimbangkan untuk handling, gudang, produksi, dan pekerjaan umum.',
      tag: 'CPR5243-2 · Size 10',
    },
    {
      image: '/products/sarung-tangan-industri-cpr5243-3.webp',
      href: cpr5243[1] ? `/produk/${cpr5243[1].slug}` : undefined,
      title: 'Sarung Tangan Industri CPR5243-3 Biru',
      desc: 'Sarung tangan kerja size 10 warna biru dengan lapisan hitam pada telapak dan ujung jari. Dapat dipertimbangkan untuk operasional pabrik, gudang, perakitan, dan bongkar muat.',
      tag: 'CPR5243-3 · Size 10',
    },
    {
      image: '/products/sarung-tangan-industri-cpr5243-4.webp',
      href: cpr5243[2] ? `/produk/${cpr5243[2].slug}` : undefined,
      title: 'Sarung Tangan Industri CPR5243-4 Hijau',
      desc: 'Sarung tangan kerja size 10 warna hijau dengan lapisan hitam pada telapak dan ujung jari. Lapisan telapak membantu cengkeraman saat menangani material.',
      tag: 'CPR5243-4 · Size 10',
    },
    {
      image: '/lp/sarung-tangan-gekko-spread.jpeg',
      title: 'Gekko Grip Nitrile',
      desc: 'Telapak berlapis nitrile untuk pekerjaan mekanikal dan penanganan komponen yang berpotensi terkena minyak ringan. Tersedia warna biru, hitam, dan putih-hitam.',
      tag: 'Gekko · Heavy duty',
    },
    {
      image: '/lp/sarung-tangan-gekko-3.jpeg',
      title: 'Gekko Grip PU',
      desc: 'Telapak dan jari berlapis PU untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Marking serta dokumen perlu diperiksa per seri sebelum PO.',
      tag: 'Gekko · PU',
    },
  ]

  if (matahari) {
    items.push({
      image: matahari.image ?? BRAND_CARD_FALLBACK_IMAGE,
      href: `/produk/${matahari.slug}`,
      title: 'Sarung Tangan Matahari',
      desc: 'Kaos tangan katun rajut merek Matahari dengan bintik PVC pada telapak dan jari. Dapat dipertimbangkan untuk kerja umum dan penanganan barang kering di gudang.',
      tag: 'Matahari · Katun rajut',
    })
  }

  if (diamond) {
    items.push({
      image: diamond.image ?? BRAND_CARD_FALLBACK_IMAGE,
      href: `/produk/${diamond.slug}`,
      title: 'Sarung Tangan Diamond',
      desc: 'Kaos tangan Diamond memakai rajutan abu-abu dengan bintik PVC kuning pada telapak dan jari. Dapat dipertimbangkan untuk kerja umum dan penanganan barang kering.',
      tag: 'Diamond · Katun rajut',
    })
  }

  items.push({
    image: '/lp/sarung-tangan-gekko-brand.jpeg',
    title: 'Sarung Tangan Bintik PVC',
    desc: 'Permukaan berbintik PVC membantu cengkeraman saat menangani barang kering di gudang.',
    tag: 'Anti-slip',
  })

  return items
}

// Quick-compare table: concrete, extractable facts (material, standard, use
// case) for featured snippets and AI answers. Claims mirror the showcase cards.
const SPEC_ROWS = [
  {
    seri: 'CPR5243-2 / 3 / 4',
    bahan: 'Rajut dengan lapisan hitam pada telapak & ujung jari',
    standar: 'Size 10',
    cocok: 'Produksi, handling, gudang, bongkar muat',
  },
  {
    seri: 'Gekko Grip Nitrile',
    bahan: 'Rajut dengan lapisan nitrile pada telapak dan jari',
    standar: 'Marking dan dokumen dikonfirmasi per seri',
    cocok: 'Bengkel, otomotif, dan komponen yang berpotensi terkena minyak ringan',
  },
  {
    seri: 'Gekko PU',
    bahan: 'Rajut dengan lapisan PU pada telapak dan jari',
    standar: 'Konfirmasi per seri',
    cocok: 'Perakitan, pengepakan, handling barang kering',
  },
  {
    seri: 'Sarung Tangan Matahari',
    bahan: 'Katun rajut, bintik PVC anti-slip',
    standar: '-',
    cocok: 'Kerja umum & gudang',
  },
  {
    seri: 'Sarung Tangan Diamond',
    bahan: 'Katun rajut, bintik PVC anti-slip',
    standar: '-',
    cocok: 'Lini produksi & gudang',
  },
  {
    seri: 'Sarung Tangan Bintik PVC',
    bahan: 'Katun rajut, bintik PVC anti-slip',
    standar: '-',
    cocok: 'Handling, angkut, bongkar muat',
  },
]

const OBJECTIONS = [
  {
    worry: 'Apakah stok dapat mendukung kebutuhan rutin?',
    answer: 'Kirim perkiraan kebutuhan bulanan, seri, dan kota tujuan. Admin akan memeriksa ketersediaan sebelum menyiapkan quotation.',
  },
  {
    worry: 'Apakah pemesanan dapat memakai PO dan invoice?',
    answer: 'Sampaikan kebutuhan PO dan dokumen administrasi saat meminta quotation. Admin akan mengonfirmasi proses yang tersedia.',
  },
  {
    worry: 'Dokumen apa yang tersedia untuk persyaratan K3?',
    answer: 'Dokumen dan standar keselamatan diperiksa per seri. Kirim persyaratan K3 atau tender Anda agar admin dapat mencocokkan barang yang tersedia.',
  },
  {
    worry: 'Bagaimana memastikan merek dan seri yang dikirim?',
    answer: 'Kami menyuplai Gekko Nitrile dan PU, serta Diamond dan Matahari untuk sarung tangan katun rajut. Sebelum memesan, Anda dapat memeriksa foto barang dan informasi setiap seri.',
  },
  {
    worry: 'Bisakah meminta quotation untuk pembandingan pemasok?',
    answer: 'Kirim seri, jumlah, dan kota tujuan untuk meminta quotation tertulis yang dapat digunakan dalam proses pengadaan.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function FactoryLandingPage() {
  const { config, testimonialImages, matahari, diamond, cpr5243Red, cpr5243Blue, cpr5243Green, newsPosts } = Route.useLoaderData()
  const showcaseItems = buildShowcaseItems(matahari, diamond, [cpr5243Red, cpr5243Blue, cpr5243Green])
  const quickShowcaseItems = [
    ...showcaseItems.filter((item) => item.href),
    ...showcaseItems.filter((item) => !item.href),
  ].slice(0, 4)

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya mau meminta quotation sarung tangan kerja untuk pabrik. Jenis pekerjaan: ___. Seri yang dipertimbangkan: ___. Kebutuhan: ___ lusin per bulan. Kota tujuan: ___. Dokumen yang diperlukan: ___. Mohon cek stok, MOQ, harga, dan ongkir.'
  const ctaMessage = 'Halo Dropify, saya mau meminta quotation sarung tangan kerja untuk pabrik. Jenis pekerjaan: ___. Seri: ___. Jumlah: ___ lusin. Kota tujuan: ___. Dokumen yang diperlukan: ___. Mohon cek stok, MOQ, harga, dan ongkir.'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Suplai sarung tangan kerja untuk kebutuhan pabrik. Stok, MOQ, dan dokumen dikonfirmasi per seri.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta penawaran"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faIndustry} className="h-3 w-3" />
              Untuk pabrik, industri, & pengadaan
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Suplai sarung tangan kerja untuk kebutuhan pabrik dan pengadaan
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Kirim jenis pekerjaan, kondisi area kerja, jumlah kebutuhan, kota tujuan, dan dokumen yang diwajibkan. Admin membantu memeriksa seri Gekko, Diamond, Matahari, atau CPR5243 yang tersedia sebelum menyiapkan quotation.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta quotation"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href="#produk-sarung-tangan-pabrik"
                onClick={() => track('lp_browse_catalog', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Cek seri dan stok dulu
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Admin akan mengonfirmasi stok, harga, minimum order, dan dokumen sesuai seri serta kebutuhan pengadaan.</p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl bg-white">
              <img src={HERO_IMAGE} alt="Suplai sarung tangan kerja untuk pabrik dari Dropify" width="800" height="800" loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-square object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                <FontAwesomeIcon icon={faRotate} className="h-4 w-4 text-slate-900" />
              </span>
              <div className="leading-tight">
                <p className="text-xs font-black uppercase tracking-wide text-slate-900">Pengadaan berkala</p>
                <p className="text-[11px] text-slate-500">Konfirmasi kebutuhan dengan admin</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="produk-sarung-tangan-pabrik" className="scroll-mt-6 border-b border-slate-100 bg-white py-10 md:py-12">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-tight text-slate-900">Mulai dari seri yang sesuai dengan pekerjaan</h2>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-600">Buka detail seri yang tersedia, atau kirim jenis pekerjaan dan jumlah kebutuhan bila Anda belum menentukan model.</p>
            </div>
            <span className="text-xs font-semibold text-slate-500">Admin mengonfirmasi stok dan ketentuan order untuk setiap seri</span>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {quickShowcaseItems.map((type) => (
              type.href ? (
                <a
                  key={type.title}
                  href={type.href}
                  onClick={() => trackSelectItem(
                    { item_id: type.title, item_name: type.title, item_category: PRODUCT_NAME },
                    'Sarung tangan pabrik quick choice',
                  )}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50"
                >
                  <span className="text-xs font-semibold text-blue-700">{type.tag}</span>
                  <h3 className="mt-1 text-sm font-black text-slate-900">{type.title}</h3>
                  <span className="mt-3 inline-flex text-xs font-bold text-blue-700">Lihat detail seri →</span>
                </a>
              ) : (
                <WhatsAppButton
                  key={type.title}
                  message={`Halo Dropify, saya mau cek ${type.title} untuk kebutuhan pabrik. Jenis pekerjaan: ___. Kebutuhan: ___. Kota tujuan: ___. Mohon cek stok dan quotation.`}
                  label={`Cek ${type.title}`}
                  itemName={type.title}
                  placement="quick_product_choice"
                  className="flex flex-col items-start rounded-xl border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:border-green-300 hover:bg-green-50"
                />
              )
            ))}
          </div>
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      {/* Who it's for — let the procurement visitor self-identify */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kebutuhan yang dapat dibahas dengan admin</h2>
            <p className="mt-2 text-base text-slate-500">Halaman ini ditujukan untuk pabrik, proyek, dan tim pengadaan yang memerlukan sarung tangan kerja dalam jumlah grosir.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Yang diperiksa sebelum quotation dibuat</h2>
            <p className="mt-2 text-base text-slate-500">Admin memakai data pekerjaan, jumlah, kota tujuan, dan kebutuhan dokumen untuk memeriksa seri yang tersedia.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works — reassure procurement on the process */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cara meminta quotation</h2>
            <p className="mt-2 text-base text-slate-500">Sampaikan kebutuhan dengan detail yang cukup agar admin dapat memeriksa barang dan pengiriman.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '1', title: 'Kirim kebutuhan', desc: 'Sebutkan jenis pekerjaan, jumlah pemakai, jumlah kebutuhan, dan kota tujuan.' },
              { n: '2', title: 'Pemeriksaan seri', desc: 'Admin memeriksa seri, stok, satuan pembelian, serta dokumen yang tersedia.' },
              { n: '3', title: 'Terima quotation', desc: 'Admin menyiapkan quotation setelah mengonfirmasi detail kebutuhan dan tujuan pengiriman.' },
              { n: '4', title: 'Konfirmasi pesanan', desc: 'Sampaikan PO atau kebutuhan administrasi sebelum pesanan diproses.' },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl border border-slate-100 bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-sm font-black text-slate-900">{step.n}</span>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Named recurring-supply offer: gives the purchasing officer a concrete
          package to say yes to, instead of an open-ended chat. */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid items-center gap-8 rounded-3xl bg-[#0B1120] px-6 py-10 md:px-10 md:py-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
                <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />
                Pengadaan berkala
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-white">Rencanakan kebutuhan sarung tangan berkala</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-300">
                Sampaikan perkiraan kebutuhan per bulan, seri yang dipertimbangkan, dan kota tujuan. Admin akan memeriksa ketersediaan saat penawaran dibuat dan sebelum pesanan berikutnya diproses.
              </p>
              <WhatsAppButton
                message={'Halo Dropify, saya mau tanya Paket Suplai Rutin sarung tangan untuk pabrik kami. Kebutuhan per bulan sekitar ___ lusin untuk ___ pegawai. Boleh dibuatkan penawarannya?'}
                label="Minta quotation suplai rutin"
                itemName="Paket Suplai Rutin"
                placement="supply_package"
                className="mt-6 inline-flex items-center gap-2.5 rounded-lg bg-green-700 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-900/30 transition-colors hover:bg-green-800"
              />
            </div>
            <ul className="space-y-3">
              {[
                'Jumlah kebutuhan dan seri yang ingin diperiksa.',
                'Kota tujuan serta target penerimaan barang.',
                'Persyaratan dokumen untuk pengadaan atau tender.',
                'Kebutuhan PO atau administrasi yang perlu dikonfirmasi.',
                'Perubahan volume atau jenis sarung tangan untuk kebutuhan berikutnya.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-xl bg-white/5 px-4 py-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                    <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />
                  </span>
                  <p className="text-base leading-relaxed text-slate-200">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Product types showcase — built from the real photos */}
      <section id="perbandingan-sarung-tangan-pabrik" className="scroll-mt-6 py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Produk sarung tangan kerja yang kami suplai</h2>
            <p className="mt-2 text-base text-slate-500">Seri industri CPR5243, Gekko, Diamond, dan Matahari dipilih sesuai jenis pekerjaan. Sampaikan area kerja, benda yang ditangani, dan risiko utamanya. Tim Dropify akan membantu menyaring seri yang relevan untuk diperiksa oleh tim Anda.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {showcaseItems.map((type) => (
              <div key={type.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                  <img src={type.image} alt={type.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                  <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{type.tag}</span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-black tracking-tight text-slate-900">{type.title}</h3>
                  <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{type.desc}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <WhatsAppButton
                      message={`Halo Dropify, pabrik kami butuh ${type.title}. Boleh minta penawaran harga & info suplai rutinnya?`}
                      label="Periksa stok dan quotation"
                      itemName={type.title}
                      className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                    />
                    {type.href && (
                      <a href={type.href} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                        Lihat detail produk →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick-compare spec table */}
          <div className="mt-10">
            <h3 className="text-lg font-black tracking-tight text-slate-900">Perbandingan cepat jenis sarung tangan pabrik</h3>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-bold">Seri / Brand</th>
                    <th className="px-4 py-3 font-bold">Bahan</th>
                    <th className="px-4 py-3 font-bold">Standar</th>
                    <th className="px-4 py-3 font-bold">Cocok untuk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {SPEC_ROWS.map((row) => (
                    <tr key={row.seri}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.seri}</td>
                      <td className="px-4 py-3 text-slate-600">{row.bahan}</td>
                      <td className="px-4 py-3 text-slate-600">{row.standar}</td>
                      <td className="px-4 py-3 text-slate-600">{row.cocok}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Harga, minimum order, dan satuan pembelian mengikuti seri yang dipilih. Minta quotation dengan menyertakan jumlah kebutuhan dan kota tujuan.
            </p>
            <p className="mt-3 text-sm text-slate-500">
              Detail seri per merek ada di halaman <a href="/sarung-tangan-gekko" className="font-semibold text-blue-600 hover:text-blue-700">sarung tangan Gekko Grip</a>,{' '}
              <a href="/sarung-tangan-diamond" className="font-semibold text-blue-600 hover:text-blue-700">sarung tangan Diamond</a>, dan{' '}
              <a href="/sarung-tangan-matahari" className="font-semibold text-blue-600 hover:text-blue-700">sarung tangan Matahari</a>.
            </p>
          </div>

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: faHelmetSafety, text: 'CPR5243: sarung tangan industri size 10, tersedia merah, biru, dan hijau' },
              { icon: faShieldHalved, text: 'Gekko: pilihan Nitrile dan PU, dokumen diperiksa per seri' },
              { icon: faHelmetSafety, text: 'Diamond dan Matahari: sarung tangan katun rajut untuk kerja umum' },
              { icon: faBoxesStacked, text: 'Ketersediaan stok dan pengiriman dikonfirmasi untuk setiap kebutuhan' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                </span>
                <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Kit K3: the paid, creditable entry product. Converts a hesitant
          chat into a low-stakes first transaction with a concrete follow-up. */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
                <FontAwesomeIcon icon={faHelmetSafety} className="h-3 w-3" />
                Sample Kit K3
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Tanyakan ketersediaan sample untuk evaluasi tim</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Jika sample diperlukan, sampaikan jenis pekerjaan dan seri yang ingin dievaluasi. Admin akan memeriksa pilihan yang tersedia.
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Biaya sample, isi, dan pengiriman dikonfirmasi sebelum pesanan sample dibuat.
              </p>
              <WhatsAppButton
                message={'Halo Dropify, saya mau menanyakan sample sarung tangan untuk evaluasi tim. Jenis pekerjaan: ___. Seri yang ingin diuji: ___. Jumlah pekerja: ___. Kota tujuan: ___. Mohon info pilihan sample, biaya, dan estimasi pengiriman.'}
                label="Tanyakan pilihan sample"
                itemName="Sample Kit K3"
                placement="sample_kit"
                className="mt-6 inline-flex items-center gap-2.5 rounded-lg bg-green-700 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-900/30 transition-colors hover:bg-green-800"
              />
            </div>
            <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6 md:p-8">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Data untuk pemeriksaan sample</p>
              <ul className="mt-4 space-y-3">
                {[
                  'Jenis pekerjaan dan benda yang dipegang',
                  'Risiko utama di area kerja',
                  'Seri atau bahan lapisan yang ingin diuji',
                  'Jumlah pekerja dan ukuran yang dibutuhkan',
                  'Dokumen yang perlu diperiksa sebelum PO',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                      <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />
                    </span>
                    <p className="text-base leading-relaxed text-slate-700">{item}</p>
                  </li>
                ))}
              </ul>
              <p className="mt-5 border-t border-slate-200 pt-4 text-sm text-slate-500">
                Admin akan mengonfirmasi produk, jumlah, biaya, dan dokumen yang tersedia sebelum pesanan sample dibuat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      {/* Objection handling — catch the unsure procurement visitor */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan sebelum pengadaan pabrik</h2>
            <p className="mt-2 text-base text-slate-500">Jawaban berikut membahas pemilihan produk, MOQ, sample, dokumen, PO, dan kebutuhan rutin.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Perlu membandingkan seri sebelum meminta quotation? Kirim jenis pekerjaan dan jumlah kebutuhan Anda.</p>
            <WhatsAppButton
              message={'Halo Dropify, saya ingin membandingkan sarung tangan kerja untuk kebutuhan pabrik. Jenis pekerjaan: ___. Jumlah kebutuhan: ___. Kota tujuan: ___.'}
              label="Tanyakan kebutuhan pengadaan"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose — works the target search terms in naturally, without
          false claims (Dropify is a factory-direct distributor, not a named
          manufacturer, so we say "langsung dari sumber produksi"). */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Supplier sarung tangan untuk kebutuhan pabrik</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify melayani kebutuhan <strong className="font-semibold text-slate-800">supplier sarung tangan</strong> dan <strong className="font-semibold text-slate-800">distributor sarung tangan</strong> untuk pabrik. Pilihan seri perlu disesuaikan dengan pekerjaan, jumlah pemakai, dan persyaratan pengadaan. Harga serta stok diperiksa saat quotation dibuat.
            </p>
            <p>
              Kami menyuplai sarung tangan kerja sesuai kondisi area dan benda yang ditangani. Lini <strong className="font-semibold text-slate-800">Gekko Nitrile</strong> biasa dipertimbangkan untuk pekerjaan mekanikal dan area yang berpotensi berminyak. <strong className="font-semibold text-slate-800">Gekko PU</strong> digunakan untuk pekerjaan kering yang membutuhkan ketangkasan tangan, seperti perakitan, pengepakan, dan handling komponen. Diamond dan Matahari tersedia untuk kebutuhan sarung tangan katun rajut harian. Untuk standar CE, EN388, food grade, atau dokumen tender, kirim persyaratannya agar admin dapat memeriksa seri yang sesuai.
            </p>
            <p>
              Kirim jenis pekerjaan, jumlah pemakai, perkiraan pemakaian per bulan, kota tujuan, serta persyaratan dokumen. Admin akan memeriksa seri, stok, satuan pembelian, harga, dan pilihan pengiriman sebelum quotation dibuat. Kebutuhan PO atau dokumen administrasi perlu disampaikan sejak awal.
            </p>
            <p>
              Halaman ini khusus untuk pengadaan pabrik. Kontraktor yang membutuhkan partai besar untuk lokasi proyek dapat melihat halaman{' '}
              <a href="/sarung-tangan-proyek" className="font-semibold text-blue-600 hover:text-blue-700">
                sarung tangan proyek
              </a>
              . Pemilik toko atau reseller yang mencari stok sarung tangan untuk dijual kembali dapat melihat halaman{' '}
              <a href="/lp/grosir-sarung-tangan" className="font-semibold text-blue-600 hover:text-blue-700">
                grosir sarung tangan
              </a>{' '}
              untuk melihat pilihan bagi reseller.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation sarung tangan pabrik</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Untuk meminta penawaran, kirim jenis pekerjaan, jumlah pengguna, kebutuhan per periode, dan kota tujuan. Tim Dropify akan memeriksa pilihan produk dan menyusun quotation.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation via WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Konsultasi kebutuhan gratis, tanpa komitmen beli.
          </p>

          {/* Legitimacy: accepted payment methods, reassures a first-time procurement buyer */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/sarung-tangan-pabrik" />

      {/* Footer with legitimacy + contact (no nav links — keeps the funnel tight) */}
      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pengadaan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar suplai sarung tangan pabrik</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'factory' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
